/**
 * Tiny inline SVG sparkline. Used on Dashboard KPI tiles. Hand-rolled
 * (no Recharts) because each tile renders independently and we don't
 * want to pull the 400 KB recharts chunk just to draw 24 datapoints.
 *
 * Build guide §3.17 — colour is severity-aware via currentColor; the
 * parent tile sets the colour and the spark inherits.
 */
import { cn } from '@/lib/utils';

interface SparkProps {
  points: number[];
  area?: boolean;
  className?: string;
}

export function Sparkline({ points, area = false, className }: SparkProps) {
  if (points.length < 2) return null;
  const W = 100;
  const H = 28;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const step = W / (points.length - 1);
  const ys = points.map((v) => H - ((v - min) / range) * H);
  const path =
    'M ' + ys.map((y, i) => `${(i * step).toFixed(1)} ${y.toFixed(1)}`).join(' L ');
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={cn('w-full block', className)}
      aria-hidden="true"
    >
      {area && (
        <path
          d={`${path} L ${W} ${H} L 0 ${H} Z`}
          fill="currentColor"
          opacity="0.10"
          stroke="none"
        />
      )}
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.25" />
    </svg>
  );
}
